// Per-page column visibility toggle. Index-based — operates on the N-th th/td
// of every row inside a target table. Persists hidden indices in localStorage.
//
// Usage:
//   mountColumnPicker(buttonHostEl, 'rounds-explorer', ['Round','When','State','Bet','Payout','Players','Inspect']);
//   applyHiddenCols(tableEl, 'rounds-explorer');  // call after each renderTable() that swaps innerHTML

function storageKey(pageKey: string): string {
  return `apx-admin:cols:${pageKey}`;
}

function loadHidden(pageKey: string): Set<number> {
  try {
    const raw = localStorage.getItem(storageKey(pageKey));
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as number[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function saveHidden(pageKey: string, hidden: Set<number>): void {
  localStorage.setItem(storageKey(pageKey), JSON.stringify([...hidden]));
}

export function applyHiddenCols(table: HTMLElement | null, pageKey: string): void {
  if (!table) return;
  const hidden = loadHidden(pageKey);
  const rows = table.querySelectorAll<HTMLTableRowElement>('tr');
  rows.forEach((row) => {
    Array.from(row.children).forEach((cell, i) => {
      (cell as HTMLElement).style.display = hidden.has(i) ? 'none' : '';
    });
  });
}

export interface ColumnPickerOpts {
  pageKey: string;
  columns: string[];
  // Optional callback after the user toggles — typically reapplies hidden
  // cols against the current table or re-renders.
  onChange?: () => void;
}

export function mountColumnPicker(host: HTMLElement, opts: ColumnPickerOpts): void {
  function rerender(): void {
    const hidden = loadHidden(opts.pageKey);
    host.innerHTML = `
      <details class="col-picker" style="position:relative">
        <summary class="ghost" style="cursor:pointer;padding:6px 10px;font-size:12px;border:1px solid var(--border);border-radius:var(--radius);background:var(--panel-2);list-style:none">⚙ Columns (${opts.columns.length - hidden.size}/${opts.columns.length})</summary>
        <div style="position:absolute;right:0;top:calc(100% + 4px);background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:8px 10px;z-index:10;min-width:180px;box-shadow:0 6px 16px rgba(0,0,0,0.3)">
          ${opts.columns
            .map(
              (label, i) => `<label style="display:flex;align-items:center;gap:8px;padding:4px 0;font-size:12px;cursor:pointer">
              <input type="checkbox" data-col-i="${i}" ${hidden.has(i) ? '' : 'checked'} />
              <span>${escapeHtml(label)}</span>
            </label>`,
            )
            .join('')}
        </div>
      </details>
    `;
    host.querySelectorAll<HTMLInputElement>('input[data-col-i]').forEach((cb) => {
      cb.addEventListener('change', () => {
        const idx = Number(cb.dataset.colI);
        const cur = loadHidden(opts.pageKey);
        if (cb.checked) cur.delete(idx);
        else cur.add(idx);
        saveHidden(opts.pageKey, cur);
        rerender();
        opts.onChange?.();
      });
    });
  }
  rerender();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
